/**
 * Sorting and due-window logic for the task list.
 *
 * Pure, and in a `.ts` module rather than beside the components that use it,
 * because vitest only collects `src/**\/*.test.ts` and `app/**\/*.test.ts` —
 * anything living in a `.tsx` file is untestable here (see CLAUDE.md → Tests).
 */

import type { Task } from '../../lib/api'

export type SortKey = 'due' | 'title' | 'status' | 'priority' | 'client'
export type SortDir = 'asc' | 'desc'

/** The Monday-morning triage windows: what's late, what's today, what's coming. */
export type DueWindow = 'all' | 'overdue' | 'today' | 'week'

export const WINDOW_LABELS: Record<DueWindow, string> = {
  all: 'All',
  overdue: 'Overdue',
  today: 'Due today',
  week: 'This week',
}

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, med: 2, low: 3 }

/** Most-urgent-first, so a status sort surfaces what's stuck before what's finished. */
const STATUS_ORDER: Record<string, number> = {
  blocked: 0,
  in_progress: 1,
  review: 2,
  todo: 3,
  done: 4,
}

/** Sorts an object key past every real value, so undated tasks sink to the bottom. */
const LAST = '￿'

type SortableTask = Pick<Task, 'due' | 'title' | 'status' | 'priority' | 'clientName'>

/**
 * Sorts a copy of `tasks`. Undated tasks always sink to the bottom regardless
 * of direction — "soonest first" and "latest first" are both questions about
 * tasks that *have* a date, and a blank sorting above a real deadline is noise.
 *
 * Done tasks sink below every open task too, whatever the key and direction
 * (LXP-51): with due-ascending as the default, a task finished last month
 * otherwise sat at the top of the list. Within the done group the chosen order
 * still applies. Sorting *by status* is the one exception — that is an explicit
 * request for the status order, which already puts done last ascending.
 */
export function sortTasks<T extends SortableTask>(tasks: T[], key: SortKey, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1

  return [...tasks].sort((a, b) => {
    if (key !== 'status') {
      const doneA = a.status === 'done' ? 1 : 0
      const doneB = b.status === 'done' ? 1 : 0
      if (doneA !== doneB) return doneA - doneB
    }
    switch (key) {
      case 'due': {
        if (!a.due && !b.due) return 0
        if (!a.due) return 1
        if (!b.due) return -1
        return a.due.localeCompare(b.due) * sign
      }
      case 'client':
        return (a.clientName ?? LAST).localeCompare(b.clientName ?? LAST) * sign
      case 'status':
        return ((STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)) * sign
      case 'priority':
        return ((PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)) * sign
      case 'title':
        return a.title.localeCompare(b.title) * sign
    }
  })
}

/**
 * Whether a task belongs in a due window. `today` is passed in rather than read
 * from the clock so this stays pure and testable.
 */
export function inWindow(
  due: string | null,
  status: string,
  dueWindow: DueWindow,
  today: string,
): boolean {
  if (dueWindow === 'all') return true
  // A finished task is not outstanding, whatever its date says.
  if (status === 'done') return false
  if (!due) return false
  if (dueWindow === 'overdue') return due < today
  if (dueWindow === 'today') return due === today
  // "This week" is the triage question — everything landing in the next seven
  // days, and anything already late, since that's what needs deciding now.
  const weekOut = new Date(Date.parse(today) + 7 * 86_400_000).toISOString().slice(0, 10)
  return due <= weekOut
}
